import { useContext, useState } from 'react';
import { useStripe } from '@stripe/react-stripe-js';

import { DepositContext } from '~/context/Deposit/DepositContext';
import { VinovestNetworkingHelper } from '~/helpers/VinovestNetworkingHelper';

/** Create new payment method for a new credit card, that will be stored for future use. */
export const useNewCreditCard = () => {
    const stripe = useStripe();

    const { setPaymentMethod, destinationPortfolioId } = useContext(DepositContext);

    const [error, setError] = useState<unknown>();
    const [loading, setLoading] = useState(false);

    const saveNewCreditCard = async (cardholderName: string, stripeCardElement: any): Promise<boolean | string> => {
        setLoading(true);
        try {
            const createMethod = await stripe?.createPaymentMethod({
                card: stripeCardElement,
                billing_details: { name: cardholderName },
                type: 'card'
            });
            if (createMethod?.error) {
                setLoading(false);
                setError(createMethod?.error.message);
            } else {
                setPaymentMethod(createMethod?.paymentMethod);
            }
        } catch (methodError) {
            setLoading(false);
            setError(methodError);
        }

        try {
            let setup;
            const { managed } = VinovestNetworkingHelper.getClient();
            const { data: result } = await managed.createStripeSecretKey(destinationPortfolioId);

            if (result?.secret) {
                setup = await stripe?.confirmCardSetup(result.secret, {
                    payment_method: {
                        card: stripeCardElement,
                        billing_details: {
                            name: cardholderName
                        }
                    }
                });
                if (setup?.error) {
                    setLoading(false);
                    setError(setup.error.message);
                    return false;
                }
                setLoading(false);
                if (typeof setup?.setupIntent.payment_method === 'string') {
                    return setup.setupIntent.payment_method;
                }
                return setup?.setupIntent.payment_method?.id || false;
            }

            setLoading(false);
            return false;
        } catch (keyError) {
            setLoading(false);
            setError(keyError);
            return false;
        }
    };

    return { saveNewCreditCard, error, loading };
};
